import { useEffect, useState } from 'react';

import { useSetIssueTypeFields } from '../providers/BulkCreateContextProvider';
import {
	useAvailableSitesState,
	useIssueTypesState,
	useMappingsActions,
	useProjectsState,
} from '../providers/BulkConfigureContextProvider';
import { FieldMetadataSchemaType, type JiraPriorities } from '../__types__/apiUtils';

import { fetchIssuesMetadata } from './apiUtils';

export const useGetIssueTypeFields = () => {
	const setIssueTypeFields = useSetIssueTypeFields();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { selectedAvailableSite } = useAvailableSitesState();
	const { selectedProject } = useProjectsState();
	const { selectedIssueType } = useIssueTypesState();
	const { setOptionsIssueTypeFields, setLoadingIssueTypeFields, setAvailablePriorities } =
		useMappingsActions();

	useEffect(() => {
		const fetchIssueMetadata = async () => {
			if (!selectedAvailableSite || !selectedProject || !selectedIssueType) {
				return;
			}

			setIsLoading(true);
			setLoadingIssueTypeFields(true);

			try {
				const data = await fetchIssuesMetadata({
					cloudId: selectedAvailableSite.cloudId,
					projectId: selectedProject.id,
					issueTypeId: selectedIssueType.id,
				});
				const mappingAvailablePriorities: JiraPriorities = [];
				if (data) {
					const requiredFields = [];
					const optionalFields = [];
					for (const field of data.fields) {
						if (field.required) {
							requiredFields.push(field);
						} else {
							optionalFields.push(field);
						}

						// Note: Currently, Jira does not support multiple priority schemas in the same issue type, so this should only be "true" once.
						if (field.schema.type === FieldMetadataSchemaType.PRIORITY) {
							const allowedValues = (field.allowedValues as JiraPriorities | undefined) ?? [];
							mappingAvailablePriorities.push(...allowedValues);
						}
					}
					void setIssueTypeFields({ requiredFields, optionalFields });
					void setOptionsIssueTypeFields([...requiredFields, ...optionalFields]);
					void setAvailablePriorities(mappingAvailablePriorities);
				}
			} catch (e: Error | unknown) {
				if (isError(e)) {
					setError(e.message);
				}
			} finally {
				setIsLoading(false);
				setLoadingIssueTypeFields(false);
			}
		};
		void fetchIssueMetadata();
	}, [
		selectedAvailableSite,
		selectedProject,
		selectedIssueType,
		setIssueTypeFields,
		setOptionsIssueTypeFields,
		setLoadingIssueTypeFields,
		setAvailablePriorities,
	]);

	return { isLoading, error };
};

const isError = (error: Error | unknown): error is Error => {
	return error instanceof Error;
};
